"use client"

import { useState, useEffect } from "react"
import Image from "next/image"
import Link from "next/link"
import { motion } from "framer-motion"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { ArrowLeft, Grid, List } from "lucide-react"
import TextReveal from "@/components/animated/text-reveal"
import GradientBackground from "@/components/animated/gradient-background"
import Breadcrumbs from "@/components/seo/breadcrumbs"
import ModernProductCard from "@/components/modern-product-card"
import MobileProductCard from "@/components/mobile/mobile-product-card"
import { resources } from "@/data/resources"
import { BookOpen, ArrowRight } from "lucide-react"

interface Category {
  id: number
  name: string
  image: string
  meta: {
    title: string
    description: string
    keywords: string
  }
  subcategories?: Array<{
    id: string
    name: string
    meta: {
      title: string
      description: string
      keywords: string
    }
  }>
}

interface Product {
  id: number
  name: string
  category: string | null
  subcategory: string | null
  image: string
  alt: string
  meta: {
    title: string
    description: string
    keywords: string
  }
}

interface CategoryDetailClientProps {
  category: Category
  products: Product[]
}

export default function CategoryDetailClient({ category, products }: CategoryDetailClientProps) {
  const [isMobile, setIsMobile] = useState(false)
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid")

  const relatedResources = resources.filter(
    (resource) =>
      resource.category.toLowerCase().includes(category.name.toLowerCase()) ||
      category.name.toLowerCase().includes(resource.category.toLowerCase())
  )

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }
    checkMobile()
    window.addEventListener("resize", checkMobile)
    return () => window.removeEventListener("resize", checkMobile)
  }, [])

  const breadcrumbItems = [
    { name: "Categories", url: "/categories" },
    { name: category.name, url: `/categories/${category.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}` },
  ]

  return (
    <div className="min-h-screen relative">
      <GradientBackground />

      <div className="container mx-auto px-4 py-8 relative z-10">
        {/* Back Button */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-6"
        >
          <Button variant="ghost" asChild className="hover:bg-white/50">
            <Link href="/categories">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Categories
            </Link>
          </Button>
        </motion.div>

        {/* Breadcrumbs */}
        <Breadcrumbs items={breadcrumbItems} />

        {/* Category Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-12"
        >
          <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg overflow-hidden">
            <CardContent className="p-0">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="relative aspect-[4/3] lg:aspect-square">
                  <Image
                    src={
                      category.image ||
                      `/placeholder.svg?height=400&width=400&text=${encodeURIComponent(category.name)}`
                    }
                    alt={category.name}
                    fill
                    className="object-cover"
                    priority
                  />
                </div>
                <div className="p-8 flex flex-col justify-center">
                  <Badge className="bg-slate-900 text-white mb-4 w-fit">{category.name}</Badge>
                  <h1 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">{category.name} Products</h1>
                  <p className="text-lg text-slate-600 leading-relaxed mb-6">{category.meta.description}</p>
                  {category.subcategories && category.subcategories.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-3">Available Types:</h3>
                      <div className="flex flex-wrap gap-2">
                        {category.subcategories.map((sub) => (
                          <Badge key={sub.id} variant="outline">
                            {sub.name}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Products Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <TextReveal>
              <h2 className="text-2xl lg:text-3xl font-bold text-slate-900">
                {products.length} Products in {category.name}
              </h2>
            </TextReveal>

            {!isMobile && (
              <div className="flex space-x-2">
                <Button
                  variant={viewMode === "grid" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setViewMode("grid")}
                >
                  <Grid className="h-4 w-4" />
                </Button>
                <Button
                  variant={viewMode === "list" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setViewMode("list")}
                >
                  <List className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>

          {products.length > 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6 }}
              className={`grid gap-6 ${isMobile
                ? "grid-cols-1 md:grid-cols-2"
                : viewMode === "grid"
                  ? "grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
                  : "grid-cols-1 md:grid-cols-2 gap-8"
                }`}
            >
              {products.map((product, index) =>
                isMobile ? (
                  <MobileProductCard key={product.id} product={product} index={index} />
                ) : (
                  <ModernProductCard key={product.id} product={product} index={index} />
                ),
              )}
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center py-16"
            >
              <h3 className="text-xl font-semibold text-slate-900 mb-2">No products found</h3>
              <p className="text-slate-600 mb-6">
                We're currently updating our {category.name.toLowerCase()} inventory.
              </p>
              <Button asChild>
                <Link href="/contact">Contact Us for Availability</Link>
              </Button>
            </motion.div>
          )}
        </div>

        {/* Related Resources for Topical Authority */}
        {relatedResources.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="mt-20 pt-20 border-t border-slate-200"
          >
            <div className="flex items-center gap-4 mb-10">
              <div className="p-3 bg-blue-600 rounded-2xl text-white">
                <BookOpen className="h-6 w-6" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-slate-900">Expert Learning & Guides</h2>
                <p className="text-slate-600">Deepen your knowledge about {category.name}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {relatedResources.map((resource) => (
                <Card key={resource.id} className="border-0 shadow-lg hover:shadow-2xl transition-all duration-300 group">
                  <CardContent className="p-8">
                    <h3 className="text-xl font-bold mb-4 group-hover:text-blue-600 transition-colors">{resource.title}</h3>
                    <p className="text-slate-600 mb-6 text-sm line-clamp-3">{resource.excerpt}</p>
                    <Link href={`/resources/${resource.slug}`} className="text-blue-600 font-semibold flex items-center text-sm">
                      Read In-Depth Guide
                      <ArrowRight className="ml-2 h-4 w-4 transform group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          </motion.div>
        )}

        {/* Mobile bottom spacing */}
        {isMobile && <div className="h-20" />}
      </div>
    </div>
  )
}
